import type {
  CatalogType,
  ReceiptFilter,
  RecoverableFilter,
} from '@/stores/frontWindowPreferencesStore';

/**
 * Formats a catalog type value from database format to display format.
 * Handles null/undefined by defaulting to 'Stock'.
 */
export const formatCatalogType = (type: string | null | undefined): CatalogType => {
  if (type === 'pnp') return 'PnP';
  if (type === 'buy program') return 'Buy Program';
  if (type === 'soc') return 'Stock';
  return 'Stock';
};

/**
 * Resolves the print heading based on active filters.
 * Priority: Type Filters > Recoverable/Non-Recoverable > Receipt Type > Default
 */
export const resolvePrintHeading = (
  receiptFilter: ReceiptFilter,
  recoverableFilter: RecoverableFilter,
  typeFilters: CatalogType[],
): string => {
  if (typeFilters.length === 1) {
    return typeFilters[0]!;
  }
  if (typeFilters.length > 1) {
    return 'Allocation Summary';
  }
  if (recoverableFilter === 'nonRecoverable') {
    return 'Non-Recoverables';
  }
  if (recoverableFilter === 'recoverable') {
    return 'Recoverables';
  }
  if (receiptFilter === 'phr') {
    return 'Permanent Hand Receipt';
  }
  if (receiptFilter === 'thr') {
    return 'Temporary Hand Receipt';
  }
  return 'Allocation Summary';
};

/**
 * Builds a human-readable summary of the filters applied to produce the report.
 */
export const buildFiltersSummary = (
  receiptFilter: ReceiptFilter,
  recoverableFilter: RecoverableFilter,
  typeFilters: CatalogType[],
  search: string,
): string => {
  const activeFilters: string[] = [];

  if (receiptFilter === 'phr') {
    activeFilters.push('Permanent hand receipt items only (PHR)');
  } else if (receiptFilter === 'thr') {
    activeFilters.push('Temporary hand receipt items only (THR)');
  }

  if (recoverableFilter === 'recoverable') {
    activeFilters.push('Recoverables only');
  } else if (recoverableFilter === 'nonRecoverable') {
    activeFilters.push('Non-Recoverables only');
  }

  if (typeFilters.length === 1) {
    activeFilters.push(`${typeFilters[0]} items only`);
  } else if (typeFilters.length > 1) {
    activeFilters.push(`Types: ${typeFilters.join(', ')}`);
  }

  const trimmedSearch = search.trim();
  if (trimmedSearch) {
    activeFilters.push(`Search contains "${trimmedSearch}"`);
  }

  if (activeFilters.length === 0) {
    return 'Filters applied: None (all allocation items included).';
  }

  return `Filters applied: ${activeFilters.join(' · ')}.`;
};
